import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import imagee from "../../assets/image/plan1.jpg";
import "./ContactUs.css";
import { Helmet } from "react-helmet";
import {
  snapSetUserData,
  trackFormView,
  trackFormStart,
  trackSignUp,
  trackClickCall,
  trackClickWhatsapp,
} from "../../utils/snap";

const TRACKING_KEYS = [
  "customer_source",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "page_url",
  "referrer",
];

function getTrackingData() {
  const params = new URLSearchParams(window.location.search);

  const sourceFromUrl =
    params.get("source") ||
    params.get("customer_source") ||
    params.get("utm_source") ||
    "";

  // ✅ لو مفيش source في الرابط يبقى website مباشرة
  const customerSource = sourceFromUrl ? sourceFromUrl.trim() : "website";

  const data = {
    customer_source: customerSource,

    // ✅ نخلي utm_source كمان website لو مفيش source
    utm_source: params.get("utm_source") || customerSource,

    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_term: params.get("utm_term") || "",
    utm_content: params.get("utm_content") || "",
    gclid: params.get("gclid") || "",
    fbclid: params.get("fbclid") || "",
    page_url: window.location.href,
    referrer: document.referrer || "",
  };

  // ✅ نخزن الداتا الحالية فقط، ومش نستخدم source قديم
  TRACKING_KEYS.forEach((key) => {
    if (data[key]) {
      sessionStorage.setItem(key, data[key]);
    }
  });

  return data;
}
function ContactUs() {
  const navigate = useNavigate();
  const location = useLocation();
  const [started, setStarted] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    document.body.classList.add("contact-body");
    trackFormView(); // SNAP form_view
    return () => document.body.classList.remove("contact-body");
  }, []);

  const formik = useFormik({
    initialValues: {
      email: "", // اختياري
      phone: "",
      city: "",
      destination_country: "",

      // Tracking fields
      utm_source: "",
      utm_medium: "",
      utm_campaign: "",
      utm_term: "",
      utm_content: "",
      gclid: "",
      fbclid: "",
      page_url: "",
      referrer: "",
      customer_source: "",
    },

    validationSchema: Yup.object({
      email: Yup.string().email("البريد غير صالح").optional(),
      phone: Yup.string()
        .required("رقم الجوال مطلوب")
        .matches(/^[0-9]+$/, "رقم الجوال يجب أن يحتوي على أرقام فقط"),
      city: Yup.string().required("المدينة مطلوبة"),
      destination_country: Yup.string().required("الدولة مطلوبة"),
    }),

    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // 1) SNAP: setUserData ثم SIGN_UP
        await snapSetUserData({
          email: values.email,
          phone: values.phone,
          countryCode: "SA",
        });

        trackSignUp();

        // 2) Tracking Data
        const trackingData = getTrackingData();

        // 3) API Payload
        const cleanValues = {
          ...values,
          phone: String(values.phone || "").replace(/\D/g, ""),

          // Send tracking fields to CRM
          ...trackingData,
        };

        const response = await axios.post(
          "https://new-tnzeem-back-av7ic.ondigitalocean.app/crm/create-customer-wordpress/",
          cleanValues,
          { headers: { "Content-Type": "application/json" } }
        );

        if (response.status === 200 || response.status === 201) {
          resetForm();
          navigate("/success");
        } else {
          throw new Error("حدث خطأ أثناء إرسال البيانات.");
        }
      } catch (error) {
        alert(
          `خطأ: ${
            error?.response?.data?.phone?.[0] ||
            error?.response?.data?.detail ||
            "فشل إرسال الطلب. حاول مرة أخرى."
          }`
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  // أول لمسة على أول فيلد → form_start
  const handleFirstInteraction = () => {
    if (!started) {
      trackFormStart();
      setStarted(true);
    }
  };

  return (
    <>
      <Helmet>
        <title>تواصل معنا | موقع التنظيم للسفر والسياحة</title>
        <meta
          name="description"
          content="تواصل معنا للحصول على أفضل الوجهات السياحية بأقوى العروض. احجز الآن واحصل على رحلات مميزة مع التنظيم للسفر والسياحة"
        />
        <link
          rel="canonical"
          href={`https://tnzeem-sa.com${location.pathname}`}
        />
      </Helmet>

      <div className="contact-page-wrapper">
        <div className="glass-card">
          <img src={imagee} alt="Holiday" className="profile-img" />

          <h1>التنظيم للسفر والسياحة</h1>

          <p className="call-text"> يتصل الان ..</p>

          <div className="call-buttons">
            <button
              className="reject-call"
              type="button"
              onClick={trackClickCall}
            >
              <i className="bi bi-telephone-x-fill"></i>
            </button>

            <button
              className="accept-call"
              type="button"
              onClick={trackClickWhatsapp}
            >
              <i className="bi bi-telephone-fill"></i>
            </button>
          </div>

          <span className="book-now">احجز الان </span>

          <form className="call-form" onSubmit={formik.handleSubmit}>
            <h5>أرسل طلبك الآن</h5>

            <label htmlFor="phone">الجوال *</label>
            <input
              ref={firstFieldRef}
              type="tel"
              id="phone"
              name="phone"
              className={
                formik.touched.phone && formik.errors.phone ? "is-invalid" : ""
              }
              placeholder="0512345678"
              value={formik.values.phone}
              onChange={(e) => {
                const onlyNumbers = e.target.value.replace(/\D/g, "");
                formik.setFieldValue("phone", onlyNumbers);
              }}
              onBlur={formik.handleBlur}
              onFocus={handleFirstInteraction}
              dir="rtl"
            />

            {formik.touched.phone && formik.errors.phone && (
              <div className="invalid-feedback">{formik.errors.phone}</div>
            )}

            <label htmlFor="city">المدينة *</label>
            <select
              id="city"
              name="city"
              className={
                formik.touched.city && formik.errors.city ? "is-invalid" : ""
              }
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onFocus={handleFirstInteraction}
            >
              <option value="">اختيار المدينة</option>
              <option value="جده">جده</option>
              <option value="المدينه">المدينه</option>
              <option value="الرياض">الرياض</option>
              <option value="آخري"> آخري</option>
            </select>

            {formik.touched.city && formik.errors.city && (
              <div className="invalid-feedback">{formik.errors.city}</div>
            )}

            <label htmlFor="destination_country">الدولة *</label>
            <select
              id="destination_country"
              name="destination_country"
              className={
                formik.touched.destination_country &&
                formik.errors.destination_country
                  ? "is-invalid"
                  : ""
              }
              value={formik.values.destination_country}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onFocus={handleFirstInteraction}
            >
              <option value="">اختيار الدولة</option>
              <option value="جورجيا">جورجيا</option>
              <option value="أذربيجان">أذربيجان</option>
              <option value="تركيا">تركيا</option>
                              <option value="تركيا جماعي">تركيا جماعي</option>

              <option value="رحلات اوروبا حرة"> رحلات اوروبا حرة</option>
              <option value="رحلات اوروبا جماعية"> رحلات اوروبا جماعية</option>
              <option value="موريشيوس">موريشيوس</option>
              <option value="المالديف">المالديف</option>
              <option value="البوسنة">البوسنة</option>
              <option value="بولندا">بولندا</option>
              <option value="ماليزيا">ماليزيا</option>
              <option value="أندونيسيا">أندونيسيا</option>
              <option value="تايلاند">تايلاند</option>
              <option value="فرنسا وسويسرا">فرنسا وسويسرا</option>
              <option value="اسبانيا">اسبانيا</option>
              <option value="ايطالياوسويسراوفرنسا">ايطاليا وسويسرا وفرنسا</option>
              <option value="النمسا والتشييك">النمسا التشييك</option>
              <option value="سويسرا وايطاليا">سويسرا وايطاليا</option>
              <option value="بريطانياواسكتلندا">بريطانيا واسكتلندا</option>
              <option value="ايطالياوالنمسا">ايطاليا والنمسا</option>
              <option value="هولنداوفرنسا">هولندا وفرنسا</option>
              <option value="كروز بحري">كروز بحري</option>
              <option value="كازاخستان">كازاخستان</option>
                            <option value="تاشيرة الشنقن - للسعودين"> تاشيرة الشنقن - للسعودين</option>

              {/*<option value="الهند">الهند</option>*/}
              {/*<option value="فنادق دبي">فنادق دبي</option> */}
              {/* <option value="دبي">دبي</option> */}
              {/* <option value="روسيا">روسيا</option> */}
              {/*<option value="ايطاليا">ايطاليا</option>*/}
              {/* <option value="سريلانكا">سريلانكا</option>*/}
              {/* <option value="المالديف">المالديف</option>
              <option value="روسيا">روسيا</option>
              <option value="فيتنام">فيتنام</option> */}
              {/* <option value="تاشيرات السعوديين">تاشيرات السعوديين</option> */}
            </select>

            {formik.touched.destination_country &&
              formik.errors.destination_country && (
                <div className="invalid-feedback">
                  {formik.errors.destination_country}
                </div>
              )}

            <button type="submit" disabled={formik.isSubmitting}>
              {formik.isSubmitting ? "جاري الإرسال..." : "إرسال الطلب"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

export default ContactUs;